// Phone number validation and formatting utilities
import { getCurrentPhoneConfig, PhoneConfig } from '../config/phoneConfig';

export interface PhoneValidationResult {
  isValid: boolean;
  formattedNumber: string;
  errorMessage?: string;
  isInternational: boolean;
}

// Legacy interface for backward compatibility
export interface PhoneValidationConfig {
  defaultCountryCode: string;
  minLength: number;
  maxLength: number;
  allowedCharacters: RegExp;
  internationalPrefix: string;
}

// Get current configuration
const currentConfig = getCurrentPhoneConfig();

// Default configuration (for backward compatibility)
export const DEFAULT_PHONE_CONFIG: PhoneValidationConfig = {
  defaultCountryCode: currentConfig.defaultCountryCode,
  minLength: currentConfig.minLength,
  maxLength: currentConfig.maxLength,
  allowedCharacters: currentConfig.allowedCharacters,
  internationalPrefix: currentConfig.internationalPrefix
};

// International phone number validation (basic)
const INTERNATIONAL_PHONE_REGEX = /^\+[1-9]\d{1,14}$/;

/**
 * Validates and formats a phone number
 * @param phoneNumber - The phone number to validate
 * @param config - Configuration options (optional)
 * @returns PhoneValidationResult
 */
export function validatePhoneNumber(
  phoneNumber: string, 
  config: Partial<PhoneValidationConfig> = {}
): PhoneValidationResult {
  const finalConfig = { ...DEFAULT_PHONE_CONFIG, ...config };
  
  // Remove all whitespace and normalize
  const cleanNumber = phoneNumber.replace(/\s+/g, '').trim();
  
  if (!cleanNumber) {
    return {
      isValid: false,
      formattedNumber: '',
      errorMessage: currentConfig.errorMessages.required,
      isInternational: false
    };
  }

  // Check if it's already an international number
  if (cleanNumber.startsWith(finalConfig.internationalPrefix)) {
    return validateInternationalNumber(cleanNumber, finalConfig);
  }

  // Check if it contains only allowed characters
  if (!finalConfig.allowedCharacters.test(cleanNumber)) {
    return {
      isValid: false,
      formattedNumber: cleanNumber,
      errorMessage: currentConfig.errorMessages.invalidCharacters,
      isInternational: false
    };
  }

  // Remove any non-digit characters for length validation
  const digitsOnly = cleanNumber.replace(/\D/g, '');
  
  // Check length
  if (digitsOnly.length < finalConfig.minLength) {
    return {
      isValid: false,
      formattedNumber: cleanNumber,
      errorMessage: currentConfig.errorMessages.tooShort,
      isInternational: false
    };
  }

  if (digitsOnly.length > finalConfig.maxLength) {
    return {
      isValid: false,
      formattedNumber: cleanNumber,
      errorMessage: currentConfig.errorMessages.tooLong,
      isInternational: false
    };
  }

  // Format the number with country code
  const formattedNumber = formatPhoneNumber(cleanNumber, finalConfig);
  
  // Validate the formatted number
  return validateFormattedNumber(formattedNumber, finalConfig);
}

/**
 * Validates an international phone number
 */
function validateInternationalNumber(
  phoneNumber: string, 
  config: PhoneValidationConfig
): PhoneValidationResult {
  if (!INTERNATIONAL_PHONE_REGEX.test(phoneNumber)) {
    return {
      isValid: false,
      formattedNumber: phoneNumber,
      errorMessage: currentConfig.errorMessages.invalidInternationalFormat,
      isInternational: true
    };
  }

  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  if (digitsOnly.length < 7 || digitsOnly.length > 15) {
    return {
      isValid: false,
      formattedNumber: phoneNumber,
      errorMessage: currentConfig.errorMessages.internationalLengthError,
      isInternational: true
    };
  }

  return {
    isValid: true,
    formattedNumber: phoneNumber,
    isInternational: true
  };
}

/**
 * Validates a formatted phone number (with country code)
 */
function validateFormattedNumber(
  phoneNumber: string, 
  config: PhoneValidationConfig
): PhoneValidationResult {
  // For numbers with the default country code, check specific patterns
  if (phoneNumber.startsWith(config.defaultCountryCode)) {
    const patterns = currentConfig.patterns;
    
    // Check mobile pattern
    if (patterns.mobile.test(phoneNumber)) {
      return {
        isValid: true,
        formattedNumber: phoneNumber,
        isInternational: false
      };
    }
    
    // Check landline pattern
    if (patterns.landline.test(phoneNumber)) {
      return {
        isValid: false,
        formattedNumber: phoneNumber,
        errorMessage: currentConfig.errorMessages.landlineNotAllowed,
        isInternational: false
      };
    }
    
    // Check toll-free numbers
    if (patterns.tollFree.test(phoneNumber)) {
      return {
        isValid: true,
        formattedNumber: phoneNumber,
        isInternational: false
      };
    }
    
    // Check emergency numbers
    if (patterns.emergency.test(phoneNumber)) {
      return {
        isValid: true,
        formattedNumber: phoneNumber,
        isInternational: false
      };
    }
    
    return {
      isValid: false,
      formattedNumber: phoneNumber,
      errorMessage: currentConfig.errorMessages.invalidFormat,
      isInternational: false
    };
  }

  return {
    isValid: true,
    formattedNumber: phoneNumber,
    isInternational: true
  };
}

/**
 * Formats a phone number with proper spacing and country code
 */
export function formatPhoneNumber(
  phoneNumber: string, 
  config: PhoneValidationConfig = DEFAULT_PHONE_CONFIG
): string {
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // If it's already an international number, return as is
  if (phoneNumber.startsWith(config.internationalPrefix)) {
    return phoneNumber;
  }
  
  // Add country code if not present
  if (!phoneNumber.startsWith(config.defaultCountryCode)) {
    return `${config.defaultCountryCode}${digitsOnly}`;
  }
  
  return phoneNumber;
}

/**
 * Formats a phone number for display with proper spacing
 */
export function formatPhoneForDisplay(phoneNumber: string): string {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Format Colombian numbers
  if (phoneNumber.startsWith('+57')) {
    const numberWithoutCode = digitsOnly.substring(2); // Remove country code
    
    if (numberWithoutCode.length === 10) {
      // Mobile: +57 3xx xxx xxxx
      if (numberWithoutCode.startsWith('3')) {
        return `+57 ${numberWithoutCode.substring(0, 3)} ${numberWithoutCode.substring(3, 6)} ${numberWithoutCode.substring(6)}`;
      }
      // Landline: +57 x xxx xxxx
      return `+57 ${numberWithoutCode.substring(0, 1)} ${numberWithoutCode.substring(1, 4)} ${numberWithoutCode.substring(4)}`;
    } else if (numberWithoutCode.length === 8) {
      // Landline: +57 x xxx xxxx
      return `+57 ${numberWithoutCode.substring(0, 1)} ${numberWithoutCode.substring(1, 4)} ${numberWithoutCode.substring(4)}`;
    }
  }
  
  // For international numbers, add spaces every 3-4 digits
  if (phoneNumber.startsWith('+')) {
    // Handle US numbers specifically
    if (phoneNumber.startsWith('+1')) {
      const numberPart = phoneNumber.substring(2);
      if (numberPart.length === 10) {
        return `+1 ${numberPart.substring(0, 3)} ${numberPart.substring(3, 6)} ${numberPart.substring(6)}`;
      }
    }
    
    // Generic international formatting
    const countryCode = phoneNumber.substring(0, 3); // Assume 2-digit country code
    const numberPart = phoneNumber.substring(3);
    
    if (numberPart.length <= 6) {
      return `${countryCode} ${numberPart}`;
    } else if (numberPart.length <= 9) {
      return `${countryCode} ${numberPart.substring(0, 3)} ${numberPart.substring(3)}`;
    } else {
      return `${countryCode} ${numberPart.substring(0, 3)} ${numberPart.substring(3, 6)} ${numberPart.substring(6)}`;
    }
  }
  
  return phoneNumber;
}

/**
 * Extracts country code from a phone number
 */
export function extractCountryCode(phoneNumber: string): string {
  if (phoneNumber.startsWith('+57')) return '+57';
  if (phoneNumber.startsWith('+1')) return '+1'; // US/Canada
  if (phoneNumber.startsWith('+44')) return '+44'; // UK
  if (phoneNumber.startsWith('+33')) return '+33'; // France
  if (phoneNumber.startsWith('+49')) return '+49'; // Germany
  if (phoneNumber.startsWith('+34')) return '+34'; // Spain
  if (phoneNumber.startsWith('+52')) return '+52'; // Mexico
  if (phoneNumber.startsWith('+55')) return '+55'; // Brazil
  if (phoneNumber.startsWith('+54')) return '+54'; // Argentina
  if (phoneNumber.startsWith('+56')) return '+56'; // Chile
  if (phoneNumber.startsWith('+51')) return '+51'; // Peru
  if (phoneNumber.startsWith('+593')) return '+593'; // Ecuador
  if (phoneNumber.startsWith('+58')) return '+58'; // Venezuela
  if (phoneNumber.startsWith('+57')) return '+57'; // Colombia
  
  // Default to Colombia
  return '+57';
}

/**
 * Checks if a phone number is Colombian
 */
export function isColombianNumber(phoneNumber: string): boolean {
  return phoneNumber.startsWith('+57') || 
         (phoneNumber.length === 10 && /^3\d{9}$/.test(phoneNumber)) ||
         (phoneNumber.length === 8 && /^[1-8]\d{7}$/.test(phoneNumber));
}

/**
 * Gets the appropriate placeholder text for phone input
 */
export function getPhonePlaceholder(config: PhoneValidationConfig = DEFAULT_PHONE_CONFIG): string {
  return currentConfig.placeholders.combined;
} 